import React, { useState, useEffect } from 'react';
import api from '../api.js';

const Tags = () => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTag, setNewTag] = useState({
    name: '',
    color: '#3B82F6',
    source: '',
    user_name: '',
    date: '',
    added: '',
    password: '',
    bucket: '',
    priority: 1
  });

  useEffect(() => {
    fetchTags();
  }, []);

  const fetchTags = async () => {
    try {
      setLoading(true);
      const data = await api.getTags();
      setTags(data.tags || []);
    } catch (err) {
      setError('Failed to load tags');
      console.error('Tags error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTag = async (e) => {
    e.preventDefault();
    try {
      await api.createTag({
        name: newTag.name,
        color: newTag.color,
        source: newTag.source,
        user_name: newTag.user_name,
        date: newTag.date,
        added: newTag.added,
        password: newTag.password,
        bucket: newTag.bucket,
        priority: newTag.priority
      });
      setNewTag({
        name: '',
        color: '#3B82F6',
        source: '',
        user_name: '',
        date: '',
        added: '',
        password: '',
        bucket: '',
        priority: 1
      });
      setShowAddForm(false);
      fetchTags();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteTag = async (tagId) => {
    if (window.confirm('Are you sure you want to delete this tag?')) {
      try {
        await api.deleteTag(tagId);
        fetchTags();
      } catch (err) {
        setError(err.message);
      }
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 1: return 'Low';
      case 2: return 'Medium-Low';
      case 3: return 'Medium';
      case 4: return 'Medium-High';
      case 5: return 'High';
      default: return 'Unknown';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 1: return 'var(--success)';
      case 2: return 'var(--info)';
      case 3: return 'var(--warning)';
      case 4: return 'var(--orange)';
      case 5: return 'var(--error)';
      default: return 'var(--text-light)';
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div style={{ fontSize: '1.2rem', color: 'var(--text-light)' }}>
            Loading tags...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h1 style={{ color: 'var(--text)', margin: 0 }}>
            Tags
          </h1>
          <button 
            className="btn btn-primary"
            onClick={() => setShowAddForm(true)}
            style={{ width: 'auto', background: 'var(--primary-color)', color: 'var(--secondary-color)' }}
          >
            + Add Tag
          </button>
        </div>
        <p style={{ color: 'var(--text-light)' }}>
          Manage your threat intelligence tags for categorizing and filtering data
        </p>
      </div>

      {error && (
        <div className="error-message" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {showAddForm && (
        <div style={{
          background: 'var(--white)',
          padding: '1.5rem',
          borderRadius: '12px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          border: '1px solid var(--border)',
          marginBottom: '2rem'
        }}>
          <h3 style={{ marginBottom: '1rem', color: 'var(--text)' }}>
            Add New Tag
          </h3>
          <form onSubmit={handleAddTag}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Tag Name *
                </label>
                <input
                  type="text"
                  value={newTag.name}
                  onChange={(e) => setNewTag({ ...newTag, name: e.target.value })}
                  placeholder="Enter tag name"
                  className="form-input"
                  required
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Color *
                </label>
                <input
                  type="color"
                  value={newTag.color}
                  onChange={(e) => setNewTag({ ...newTag, color: e.target.value })}
                  className="form-input"
                  style={{ height: '40px', padding: '0.25rem' }}
                  required
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Priority
                </label>
                <select
                  value={newTag.priority}
                  onChange={(e) => setNewTag({ ...newTag, priority: parseInt(e.target.value) })}
                  className="form-input"
                >
                  <option value={1}>Low</option>
                  <option value={2}>Medium-Low</option>
                  <option value={3}>Medium</option>
                  <option value={4}>Medium-High</option>
                  <option value={5}>High</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Source
                </label>
                <input
                  type="text"
                  value={newTag.source}
                  onChange={(e) => setNewTag({ ...newTag, source: e.target.value })}
                  placeholder="Data source"
                  className="form-input"
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  User
                </label>
                <input
                  type="text"
                  value={newTag.user_name}
                  onChange={(e) => setNewTag({ ...newTag, user_name: e.target.value })}
                  placeholder="Associated user"
                  className="form-input"
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Date
                </label>
                <input
                  type="text"
                  value={newTag.date}
                  onChange={(e) => setNewTag({ ...newTag, date: e.target.value })}
                  placeholder="Date pattern"
                  className="form-input"
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Added
                </label>
                <input
                  type="text"
                  value={newTag.added}
                  onChange={(e) => setNewTag({ ...newTag, added: e.target.value })}
                  placeholder="Added date pattern"
                  className="form-input"
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Password
                </label>
                <input
                  type="text"
                  value={newTag.password}
                  onChange={(e) => setNewTag({ ...newTag, password: e.target.value })}
                  placeholder="Password pattern"
                  className="form-input"
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Bucket
                </label>
                <input
                  type="text"
                  value={newTag.bucket}
                  onChange={(e) => setNewTag({ ...newTag, bucket: e.target.value })}
                  placeholder="Bucket name"
                  className="form-input"
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
              <button type="submit" className="btn btn-primary" style={{ width: 'auto', background: 'var(--primary-color)', color: 'var(--secondary-color)' }}>
                Add Tag
              </button>
              <button
                type="button"
                className="btn btn-link"
                onClick={() => setShowAddForm(false)}
                style={{ width: 'auto' }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div style={{
        background: 'var(--white)',
        borderRadius: '12px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        border: '1px solid var(--border)',
        overflow: 'hidden'
      }}>
        {tags.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--secondary-color)' }}>
                  <th style={{ textAlign: 'left', padding: '1rem', color: 'var(--text-light)', fontWeight: '600' }}>
                    Tag
                  </th>
                  <th style={{ textAlign: 'left', padding: '1rem', color: 'var(--text-light)', fontWeight: '600' }}>
                    Priority
                  </th>
                  <th style={{ textAlign: 'left', padding: '1rem', color: 'var(--text-light)', fontWeight: '600' }}>
                    Source
                  </th>
                  <th style={{ textAlign: 'left', padding: '1rem', color: 'var(--text-light)', fontWeight: '600' }}>
                    User
                  </th>
                  <th style={{ textAlign: 'left', padding: '1rem', color: 'var(--text-light)', fontWeight: '600' }}>
                    Password
                  </th>
                  <th style={{ textAlign: 'left', padding: '1rem', color: 'var(--text-light)', fontWeight: '600' }}>
                    Infection Date
                  </th>
                  <th style={{ textAlign: 'left', padding: '1rem', color: 'var(--text-light)', fontWeight: '600' }}>
                    Discovered
                  </th>
                  <th style={{ textAlign: 'left', padding: '1rem', color: 'var(--text-light)', fontWeight: '600' }}>
                    Bucket
                  </th>
                  <th style={{ textAlign: 'left', padding: '1rem', color: 'var(--text-light)', fontWeight: '600' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {tags.map((tag, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '1rem' }} title={tag['name']}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div
                          style={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '4px',
                            backgroundColor: tag['color'] || '#3B82F6',
                            flexShrink: 0
                          }}
                        ></div>
                        <span style={{ fontWeight: '500' }}>{tag['name']}</span>
                      </div>
                    </td>
                    <td style={{ padding: '1rem' }} title={getPriorityLabel(tag['priority'])}>
                      <span style={{
                        color: getPriorityColor(tag['priority']),
                        fontWeight: '500'
                      }}>
                        {getPriorityLabel(tag['priority'])}
                      </span>
                    </td>
                    <td style={{ padding: '1rem' }} title={tag['source'] || '-'}>{tag['source'] || '-'}</td>
                    <td style={{ padding: '1rem' }} title={tag['user_name'] || '-'}>{tag['user_name'] || '-'}</td>
                    <td style={{ padding: '1rem' }} title={tag['password'] || '-'}>{tag['password'] || '-'}</td>
                    <td style={{ padding: '1rem' }} title={tag['date'] || '-'}>{tag['date'] || '-'}</td>
                    <td style={{ padding: '1rem' }} title={tag['added'] || '-'}>{tag['added'] || '-'}</td>
                    <td style={{ padding: '1rem' }} title={tag['bucket'] || '-'}>{tag['bucket'] || '-'}</td>
                    <td style={{ padding: '1rem' }}>
                      <button
                        className="btn btn-link"
                        onClick={() => handleDeleteTag(tag['id'])}
                        style={{
                          padding: '0.5rem',
                          fontSize: '0.875rem',
                          color: 'var(--error)'
                        }}
                        title="Delete tag"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '3rem',
            color: 'var(--text-light)'
          }}>
            <i className="fas fa-tags" style={{ fontSize: '3rem', marginBottom: '1rem' }}></i>
            <h3 style={{ marginBottom: '0.5rem', color: 'var(--text)' }}>
              No tags found
            </h3>
            <p style={{ marginBottom: '1.5rem' }}>
              Create tags to categorize and filter your threat intelligence data
            </p>
            <button
              className="btn btn-primary"
              onClick={() => setShowAddForm(true)}
              style={{ width: 'auto' }}
            >
              <i className="fas fa-plus" style={{ marginRight: '0.5rem' }}></i>
              Create Your First Tag
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Tags;
